import React, { useState, useEffect } from 'react';
import {
  CurrencyDollarIcon,
  EyeIcon,
  EyeSlashIcon,
  ArrowPathIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { getApiUrl } from '../utils/api';

interface Balance {
  asset: string;
  free: number;
  locked: number;
  total: number;
  usdPrice?: number;
  usdValue?: number;
  krwPrice?: number;
  krwValue?: number;
}

interface BalanceResponse {
  success?: boolean;
  error?: string;
  balances: Balance[];
  totalAssets?: number;
  totalUsdValue?: number;
  totalKrwValue?: number;
  updateTime?: string;
  isTestnet?: boolean;
}

interface Settings {
  exchange: string;
  currency: string;
}

const BinanceBalance: React.FC = () => {
  console.log('💰 BinanceBalance 컴포넌트 렌더링 중...');
  
  const [balances, setBalances] = useState<Balance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [showSmallBalances, setShowSmallBalances] = useState(false);
  const [isTestnet, setIsTestnet] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<string>('');
  const [refreshing, setRefreshing] = useState(false);
  const [totalUsdValue, setTotalUsdValue] = useState<number>(0);
  const [totalKrwValue, setTotalKrwValue] = useState<number>(0);
  const [settings, setSettings] = useState<Settings>({ exchange: 'binance', currency: 'USD' });

  // 주요 자산 목록 (우선 표시)
  const priorityAssets = ['USDT', 'BTC', 'ETH', 'BNB', 'SOL'];

  // 소액 잔고 임계값 (USD/KRW 기준)
  const smallBalanceThreshold = 1.0;

  // 거래소별 정보
  const getExchangeInfo = (exchange: string) => {
    const exchangeMap: { [key: string]: { name: string; icon: string; color: string } } = {
      'binance': { name: '바이낸스', icon: '🟡', color: 'text-yellow-500' },
      'upbit': { name: '업비트', icon: '🔵', color: 'text-blue-500' },
      'bithumb': { name: '빗썸', icon: '🟠', color: 'text-orange-500' }
    };
    
    return exchangeMap[exchange] || { name: '거래소', icon: '💱', color: 'text-gray-500' };
  };

  // 화폐 단위별 정보
  const getCurrencyInfo = (currency: string) => {
    const currencyMap: { [key: string]: { symbol: string; name: string } } = {
      'USD': { symbol: '$', name: '달러' },
      'KRW': { symbol: '₩', name: '원' }
    };
    
    return currencyMap[currency] || { symbol: '$', name: '달러' };
  };

  // 설정 불러오기
  const fetchSettings = async () => {
    try {
      const response = await fetch(getApiUrl() + '/api/settings');
      const data = await response.json();
      setSettings({
        exchange: data.exchange || 'binance',
        currency: data.currency || 'USD'
      });
    } catch (err) {
      console.error('설정 불러오기 오류:', err);
    }
  };

  const fetchBalances = async () => {
    console.log('💰 잔고 조회 시작...');
    try {
      setRefreshing(true);
      const response = await fetch(getApiUrl() + '/api/binance/balance');
      const data: BalanceResponse = await response.json();
      
      console.log('💰 잔고 응답:', data);
      
      if (data.error) {
        setError(data.error);
        setBalances([]);
        setTotalUsdValue(0);
        setTotalKrwValue(0);
      } else {
        setBalances(data.balances || []);
        setIsTestnet(data.isTestnet || false);
        setLastUpdate(data.updateTime || new Date().toISOString());
        setTotalUsdValue(data.totalUsdValue || 0);
        setTotalKrwValue(data.totalKrwValue || 0);
        setError('');
      }
    } catch (err) {
      console.error('잔고 조회 오류:', err);
      setError('잔고를 불러오는 중 오류가 발생했습니다.');
      setBalances([]);
      setTotalUsdValue(0);
      setTotalKrwValue(0);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    console.log('💰 BinanceBalance useEffect 실행, 설정 및 잔고 조회 시작...');
    fetchSettings();
    fetchBalances();
    
    // 30초마다 자동 새로고침
    const interval = setInterval(fetchBalances, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const formatBalance = (amount: number, asset: string) => {
    if (amount === 0) return '0';
    
    // 자산별 소수점 자리수 설정
    let decimals = 4;
    if (asset === 'BTC' || asset === 'ETH') decimals = 8;
    else if (asset === 'USDT' || asset === 'USDC' || asset === 'BUSD') decimals = 2;
    else if (asset === 'SOL' || asset === 'BNB') decimals = 4;
    
    // 매우 작은 값 처리 (0.000001 미만)
    if (amount < 0.000001) {
      return amount.toExponential(2);
    }
    
    // 큰 숫자는 적은 소수점으로 표시
    if (amount >= 1000000) {
      decimals = Math.min(decimals, 2);
    } else if (amount >= 1000) {
      decimals = Math.min(decimals, 4);
    } else if (amount >= 1) {
      decimals = Math.min(decimals, 6);
    }
    
    // 숫자 포맷팅 (콤마 포함)
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimals,
      useGrouping: true
    }).format(amount);
  };

  const formatCurrency = (amount: number, currency: string) => {
    const currencyInfo = getCurrencyInfo(currency);
    
    if (currency === 'KRW') {
      return new Intl.NumberFormat('ko-KR', {
        style: 'currency',
        currency: 'KRW',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount);
    } else {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
    }
  };

  const getAssetIcon = (asset: string) => {
    const icons: { [key: string]: string } = {
      'BTC': '₿',
      'ETH': 'Ξ',
      'BNB': '𝔹',
      'SOL': '◎',
      'USDT': '$',
      'USDC': '$',
      'BUSD': '$'
    };
    
    return icons[asset] || '◦';
  };

  const getAssetColor = (asset: string) => {
    const colors: { [key: string]: string } = {
      'BTC': 'text-orange-500',
      'ETH': 'text-blue-500',
      'BNB': 'text-yellow-500',
      'SOL': 'text-purple-500',
      'USDT': 'text-green-500',
      'USDC': 'text-blue-600',
      'BUSD': 'text-yellow-600'
    };
    
    return colors[asset] || 'text-gray-500';
  };

  // 잔고를 주요 자산과 소액 자산으로 분류
  const categorizeBalances = () => {
    const priorityBalances: Balance[] = [];
    const smallBalances: Balance[] = [];
    const otherBalances: Balance[] = [];

    balances.forEach(balance => {
      if (priorityAssets.includes(balance.asset)) {
        priorityBalances.push(balance);
      } else if (balance.total < smallBalanceThreshold) {
        smallBalances.push(balance);
      } else {
        otherBalances.push(balance);
      }
    });

    // 우선순위에 따라 정렬
    priorityBalances.sort((a, b) => {
      return priorityAssets.indexOf(a.asset) - priorityAssets.indexOf(b.asset);
    });

    // 다른 자산들은 잔고 크기순으로 정렬
    otherBalances.sort((a, b) => b.total - a.total);
    smallBalances.sort((a, b) => b.total - a.total);

    return { priorityBalances, otherBalances, smallBalances };
  };

  const { priorityBalances, otherBalances, smallBalances } = categorizeBalances();
  const exchangeInfo = getExchangeInfo(settings.exchange);
  const currencyInfo = getCurrencyInfo(settings.currency);
  const totalValue = settings.currency === 'KRW' ? totalKrwValue : totalUsdValue;

  const formatTime = (isoString: string) => {
    try {
      const date = new Date(isoString);
      return date.toLocaleTimeString('ko-KR', { 
        hour: '2-digit', 
        minute: '2-digit',
        second: '2-digit'
      });
    } catch {
      return '';
    }
  };

  if (loading) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center">
            <CurrencyDollarIcon className="h-5 w-5 text-green-500 mr-2" />
            {exchangeInfo.icon} {exchangeInfo.name} 잔고
          </h3>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <ArrowPathIcon className="h-8 w-8 text-blue-500 animate-spin mx-auto mb-2" />
            <p className="text-sm text-gray-600 dark:text-gray-400">잔고 조회 중...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center">
            <CurrencyDollarIcon className="h-5 w-5 text-green-500 mr-2" />
            {exchangeInfo.icon} {exchangeInfo.name} 잔고
          </h3>
          <button
            onClick={fetchBalances}
            disabled={refreshing}
            className="p-2 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-all"
          >
            <ArrowPathIcon className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
        
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <InformationCircleIcon className="h-8 w-8 text-red-500 mx-auto mb-2" />
            <p className="text-sm text-red-600 dark:text-red-400 mb-2">{error}</p>
            <button
              onClick={fetchBalances}
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
            >
              다시 시도
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center">
            <CurrencyDollarIcon className="h-5 w-5 text-green-500 mr-2" />
            {exchangeInfo.icon} {exchangeInfo.name} 잔고
          </h3>
          {isTestnet && (
            <span className="ml-2 px-2 py-1 bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300 text-xs font-medium rounded">
              TESTNET
            </span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowSmallBalances(!showSmallBalances)}
            className="p-2 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-all"
            title={showSmallBalances ? "소액 잔고 숨기기" : "소액 잔고 보기"}
          >
            {showSmallBalances ? (
              <EyeSlashIcon className="h-4 w-4" />
            ) : (
              <EyeIcon className="h-4 w-4" />
            )}
          </button>
          <button
            onClick={fetchBalances}
            disabled={refreshing}
            className="p-2 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-all"
            title="새로고침"
          >
            <ArrowPathIcon className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Total Value Summary */}
      {totalValue > 0 && (
        <div className="mb-4 bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 rounded-lg p-4 border border-green-200 dark:border-green-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <CurrencyDollarIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
              <span className="text-sm font-medium text-gray-600 dark:text-gray-300">총 자산 가치</span>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-green-700 dark:text-green-400">
                {formatCurrency(totalValue, settings.currency)}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {balances.length}개 자산
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Balance List */}
      <div className="flex-1 overflow-y-auto space-y-2">
        {/* Priority Assets */}
        {priorityBalances.map((balance) => (
          <div
            key={balance.asset}
            className="bg-white dark:bg-slate-700 rounded-lg p-3 border border-slate-200 dark:border-slate-600 shadow-sm"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className={`text-xl ${getAssetColor(balance.asset)}`}>
                  {getAssetIcon(balance.asset)}
                </div>
                <div>
                  <div className="font-semibold text-slate-900 dark:text-white">
                    {balance.asset}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="font-semibold text-slate-900 dark:text-white">
                  {formatBalance(balance.total, balance.asset)}
                </div>
                <div className="text-sm text-gray-500">
                  {formatCurrency(
                    settings.currency === 'KRW' ? (balance.krwValue || 0) : (balance.usdValue || 0), 
                    settings.currency
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Other Significant Assets */}
        {otherBalances.map((balance) => (
          <div
            key={balance.asset}
            className="bg-white dark:bg-slate-700 rounded-lg p-3 border border-slate-200 dark:border-slate-600 shadow-sm"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className={`text-lg ${getAssetColor(balance.asset)}`}>
                  {getAssetIcon(balance.asset)}
                </div>
                <div>
                  <div className="font-semibold text-slate-900 dark:text-white">
                    {balance.asset}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="font-semibold text-slate-900 dark:text-white">
                  {formatBalance(balance.total, balance.asset)}
                </div>
                <div className="text-sm text-gray-500">
                  {formatCurrency(
                    settings.currency === 'KRW' ? (balance.krwValue || 0) : (balance.usdValue || 0), 
                    settings.currency
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Small Balances (collapsible) */}
        {showSmallBalances && smallBalances.length > 0 && (
          <>
            <div className="pt-2 border-t border-slate-200 dark:border-slate-600">
              <div className="text-sm text-gray-500 mb-2 px-1">
                소액 잔고 ({smallBalances.length}개)
              </div>
              {smallBalances.map((balance) => (
                <div
                  key={balance.asset}
                  className="bg-slate-50 dark:bg-slate-800 rounded-lg p-2 mb-1 border border-slate-100 dark:border-slate-700"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className={`text-sm ${getAssetColor(balance.asset)}`}>
                        {getAssetIcon(balance.asset)}
                      </div>
                      <div className="text-sm font-medium text-slate-900 dark:text-white">
                        {balance.asset}
                      </div>
                    </div>
                    <div className="text-sm text-slate-900 dark:text-white">
                      {formatBalance(balance.total, balance.asset)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Empty State */}
        {balances.length === 0 && (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <CurrencyDollarIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-500 dark:text-gray-400">잔고가 없습니다</p>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      {lastUpdate && (
        <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-600">
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>총 {balances.length}개 자산</span>
            <span>업데이트: {formatTime(lastUpdate)}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default BinanceBalance; 